import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Button,
    Box,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Text,
    Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { getCroppedImg } from '../../../utils/cropImage';

const ASPECT_RATIO = 16 / 9;

const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete }) => {
    const { t } = useTranslation('default');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleCropComplete = useCallback((_, pixels) => {
        setCroppedAreaPixels(pixels);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels) return;
        setIsLoading(true);
        try {
            const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels);
            onCropComplete(croppedFile);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={false}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{t('image.crop_title')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                    <Box position="relative" w="full" h="340px" bg="gray.100">
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={ASPECT_RATIO}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleCropComplete}
                        />
                    </Box>
                    <Box mt={4} px={1}>
                        <Flex align="center" gap={3}>
                            <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                                {t('image.crop_zoom')}
                            </Text>
                            <Slider
                                min={1}
                                max={3}
                                step={0.05}
                                value={zoom}
                                onChange={setZoom}
                                aria-label="zoom"
                            >
                                <SliderTrack>
                                    <SliderFilledTrack bg="brand.500" />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                        </Flex>
                    </Box>
                </ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={handleClose} borderRadius={'12px'}>
                        {t('image.crop_cancel')}
                    </Button>
                    <Button
                        bg="brand.500"
                        color="white"
                        onClick={handleSave}
                        isLoading={isLoading}
                        borderRadius={'12px'}
                        _hover={{ bg: 'brand.600' }}
                    >
                        {t('image.crop_save')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ImageCropModal;
